/**
 * Locale Cookie Utilities
 * Handles cookie-based locale persistence for both server and client
 */

import { SUPPORTED_LOCALES, type SupportedLocale } from './types'

export const LOCALE_COOKIE_NAME = 'chatbotty_locale'
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year in seconds

/** Request header carrying the render locale, set by proxy.ts and read by the root layout. */
export const LOCALE_HEADER_NAME = 'x-locale'

/**
 * Locale defaults mapping
 */
export const LOCALE_DEFAULTS = {
  cs: { currency: 'CZK' as const, timezone: 'Europe/Prague' },
  en: { currency: 'USD' as const, timezone: 'America/New_York' },
  de: { currency: 'EUR' as const, timezone: 'Europe/Berlin' },
  fr: { currency: 'EUR' as const, timezone: 'Europe/Paris' },
  it: { currency: 'EUR' as const, timezone: 'Europe/Rome' },
  nl: { currency: 'EUR' as const, timezone: 'Europe/Amsterdam' },
  pl: { currency: 'EUR' as const, timezone: 'Europe/Warsaw' },
  pt: { currency: 'USD' as const, timezone: 'America/Sao_Paulo' },
  es: { currency: 'USD' as const, timezone: 'America/Mexico_City' }
} as const

export type LocaleCurrency = 'CZK' | 'USD' | 'EUR'

/**
 * Parse locale cookie from cookie string (server-side)
 */
export function parseLocaleCookie(cookieString: string | null): SupportedLocale | null {
  if (!cookieString) return null

  const cookies = cookieString.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=')
    if (key && value) {
      acc[key] = decodeURIComponent(value)
    }
    return acc
  }, {} as Record<string, string>)

  const locale = cookies[LOCALE_COOKIE_NAME]
  if (locale && SUPPORTED_LOCALES.includes(locale as SupportedLocale)) {
    return locale as SupportedLocale
  }
  return null
}

/**
 * Get locale from cookie (client-side)
 */
export function getLocaleCookieClient(): SupportedLocale | null {
  if (typeof document === 'undefined') return null
  return parseLocaleCookie(document.cookie)
}

/**
 * Set locale cookie (client-side)
 */
export function setLocaleCookieClient(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return

  const expires = new Date()
  expires.setTime(expires.getTime() + LOCALE_COOKIE_MAX_AGE * 1000)

  document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
}

/**
 * Detect browser locale from navigator.language
 * Returns 'cs' for Czech, 'en' for everything else (default)
 */
export function detectBrowserLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return 'en'

  const browserLang = navigator.language || (navigator as any).userLanguage || ''
  const primaryLang = browserLang.split('-')[0].toLowerCase()

  const langMap: Record<string, SupportedLocale> = {
    cs: 'cs', de: 'de', fr: 'fr', it: 'it', nl: 'nl', pl: 'pl', pt: 'pt', es: 'es'
  }

  return langMap[primaryLang] || 'en'
}

/**
 * Parse Accept-Language header (server-side)
 * Returns 'cs' for Czech, 'en' for everything else (default)
 */
export function parseAcceptLanguage(acceptLanguage: string | null): SupportedLocale {
  if (!acceptLanguage) return 'en'

  // Accept-Language format: "cs,en;q=0.9,sk;q=0.8"
  const languages = acceptLanguage.split(',').map(lang => {
    const [code, qValue] = lang.trim().split(';q=')
    return {
      code: code.split('-')[0].toLowerCase(),
      q: qValue ? parseFloat(qValue) : 1.0
    }
  })

  // Sort by quality value
  languages.sort((a, b) => b.q - a.q)

  const langMap: Record<string, SupportedLocale> = {
    cs: 'cs', de: 'de', fr: 'fr', it: 'it', nl: 'nl', pl: 'pl', pt: 'pt', es: 'es'
  }

  // Find first supported language
  for (const lang of languages) {
    if (langMap[lang.code]) return langMap[lang.code]
  }

  return 'en' // Default to English
}

/**
 * Get currency for locale
 */
export function getCurrencyForLocale(locale: SupportedLocale): LocaleCurrency {
  return LOCALE_DEFAULTS[locale].currency
}

/**
 * Get timezone for locale
 */
export function getTimezoneForLocale(locale: SupportedLocale): string {
  return LOCALE_DEFAULTS[locale].timezone
}
